import { z } from "zod";

import { fetchPreRequisiteCourseStatus } from "@/services/badgingService/badging";
import http from "@/services/http";
import { EventBus } from "@/store/eventBus";
import store from "@/store/index";
import dateFormats from "@/utils/dateFormats";

const productError =
  "There was an error adding the item to your cart. Please try again!";

const preRequisiteCourseError =
  "Your Credly profile doesn't indicate that you completed the Ethical Hacker prerequisite course.";

const itemAddedToCart = "This item was added to your cart.";

export async function add(email: string, productId: string) {
  const { data } = await http().post(
    window.env.API_COMMERCE + "/commerce/basket",
    {
      product: {
        customerInfo: {
          email: email,
        },
        productItems: [
          {
            productId: productId,
            quantity: 1,
          },
        ],
      },
      learner: {
        productId: productId,
        quantity: 1,
        c_learnerEmails: "{" + email + "}",
      },
    }
  );
  return data;
}

export async function getBasket(basketId: string) {
  const { data } = await http().get(
    window.env.API_COMMERCE + "/commerce/basket/" + basketId
  );
  calculateCartItem(data);
  return data;
}

async function calculateCartItem(data: any) {
  let counter = 0;
  if (data && data.basketId != "" && data.productItems) {
    data.productItems.forEach(function (product: any) {
      if (product) {
        counter += product.quantity;
      }
    });
  }
  EventBus.emit("updateQty", counter);
}

export async function addToCart(productId: string, quantity: number) {
  const basketId = localStorage.getItem("BASKET_ID");
  await http()
    .post(window.env.API_COMMERCE + "/commerce/basket/products", {
      basket: {
        basketId: basketId,
      },
      products: [
        {
          productId: productId,
          quantity: quantity,
        },
      ],
    })
    .then((data: any) => {
      calculateCartItem(data.data);
      const productItems = data.data.productItems.map(
        (item: any, index: number) => {
          return {
            productId: item.productId,
            itemId: item.itemId,
            productTitle: item.itemText,
            productPrice: item.price,
            basePrice: item.basePrice,
            productQuantity: item.quantity,
            "line-num": index,
          };
        }
      );

      EventBus.emit("cartTelemetry", {
        productItems,
        basketId: data.data.basketId,
      });

      EventBus.emit("showBanner", {
        type: "SUCCESS",
        title: "Success: ",
        message: itemAddedToCart,
      });
    })
    .catch((error: any) => {
      EventBus.emit("showBanner", {
        type: "ERROR",
        title: "Error: ",
        message: productError,
      });
    });
}

export async function updateProduct(productId: string, quantity: number) {
  const basketId = localStorage.getItem("BASKET_ID");
  const { data } = await http().post(
    window.env.API_COMMERCE + "/commerce/basket/products",
    {
      basket: {
        basketId: basketId,
      },
      products: [
        {
          productId: productId,
          quantity: quantity,
        },
      ],
    }
  );
  return data;
}

export async function validateAndAddProduct(
  productId: string,
  quantity: number,
  email: string,
  productType: string = "ALL"
) {
  const systemDowntime = store.getters["commerce/systemDowntime"];
  if (
    systemDowntime?.status == "ENABLED" &&
    dateFormats.isNowWithinRange(
      systemDowntime.startDate,
      systemDowntime.endDate
    )
  ) {
    EventBus.emit("showBanner", {
      type: "ERROR",
      title: "Error: ",
      message: systemDowntime?.message,
    });
    return;
  }
  if (productType == "CTF" || productType?.toLocaleLowerCase() == "challenge") {
    fetchPreRequisiteCourseStatus(productId)
      .then((response: any) => {
        if (response?.valid) {
          return addValidatedProductToCart(productId, quantity);
        } else {
          EventBus.emit("showBanner", {
            type: "ERROR",
            title: "Error: ",
            message: preRequisiteCourseError,
          });
        }
      })
      .catch(() => {
        EventBus.emit("showBanner", {
          type: "ERROR",
          title: "Error: ",
          message: preRequisiteCourseError,
        });
      });
  } else {
    return addValidatedProductToCart(productId, quantity);
  }
}

export async function addValidatedProductToCart(
  productId: string,
  quantity: number
) {
  const basketId = localStorage.getItem("BASKET_ID");
  if (basketId != "" && basketId != null && basketId != "NA") {
    await getBasket(basketId)
      .then((data: any) => {
        if (data && data.basketId != "" && basketId == data.basketId) {
          localStorage.setItem("BASKET_ID", data.basketId);
          return validateCTFProduct(data, productId, quantity);
        } else {
          return createBasket(productId, quantity);
        }
      })
      .catch((error: any) => {
        if (
          ["error while reading the token", "clear the cart"].includes(
            error?.response?.data?.error
          )
        ) {
          return createBasket(productId, quantity);
        }
        EventBus.emit("showBanner", {
          type: "ERROR",
          title: "Error: ",
          message: productError,
        });
        return error;
      });
  } else {
    return createBasket(productId, quantity);
  }
}

function validateCTFProduct(data: any, productId: string, quantity: number) {
  if (data?.productItems) {
    let ctfProductFound = false;
    data.productItems.forEach(function (product: any) {
      if (
        product.productId == productId &&
        product.c_product_type == "CTF" &&
        product.quantity > 0
      ) {
        ctfProductFound = true;
      }
    });
    if (!ctfProductFound) {
      return addToCart(productId, quantity);
    } else {
      EventBus.emit("showBanner", {
        type: "ERROR",
        title: "Error: ",
        message: "You cannot purchase multiple quantity of this product.",
      });
    }
  } else {
    return addToCart(productId, quantity);
  }
}

async function createBasket(productId: string, quantity: number) {
  await http()
    .get(window.env.API_COMMERCE + "/commerce/login")
    .then((data: any) => {
      if (data.data && data.data.basketId != "") {
        localStorage.setItem("BASKET_ID", data.data.basketId);
        return validateCTFProduct(data, productId, quantity);
      }
    })
    .catch((error: any) => {
      EventBus.emit("showBanner", {
        type: "ERROR",
        title: "Error: ",
        message: productError,
      });
      return error;
    });
}

export async function addLearnerInformation(learners: any, admins: any[] = []) {
  const basketId = localStorage.getItem("BASKET_ID");
  const { data } = await http().post(
    window.env.API_COMMERCE + "/commerce/basket/learners",
    {
      basket: {
        basketId: basketId,
      },
      learners: learners,
      customerAdmins: admins,
    }
  );
  return data;
}

export async function removeProduct(itemId: string) {
  const basketId = localStorage.getItem("BASKET_ID");
  const { data } = await http().post(
    window.env.API_COMMERCE + "/commerce/basket/remove-product",
    {
      basket: {
        basketId: basketId,
      },
      itemId: itemId,
    }
  );
  calculateCartItem(data);
  return data;
}

export async function updateProductLineItem(lineItem: any) {
  const basketId = localStorage.getItem("BASKET_ID");
  const { data } = await http().post(
    window.env.API_COMMERCE + "/commerce/basket/update-product",
    {
      basket: {
        basketId: basketId,
      },
      lineItem: lineItem,
    }
  );
  calculateCartItem(data);
  return data;
}

export async function loginUser() {
  const { data } = await http().get(
    window.env.API_COMMERCE + "/commerce/login"
  );
  calculateCartItem(data);
  return data;
}

export async function applyDiscountCoupon(coupon: any) {
  const basketId = localStorage.getItem("BASKET_ID");
  const { data } = await http().post(
    window.env.API_COMMERCE + "/commerce/basket/apply-coupon",
    {
      basket: {
        basketId: basketId,
      },
      coupon: coupon,
    }
  );
  return data;
}

export async function removeDiscountCoupon(itemId: any) {
  const basketId = localStorage.getItem("BASKET_ID");
  const { data } = await http().post(
    window.env.API_COMMERCE + "/commerce/basket/remove-coupon",
    {
      basket: {
        basketId: basketId,
      },
      itemId: itemId,
    }
  );
  return data;
}

export async function validateBasket(basketId: string) {
  const { data } = await http().get(
    window.env.API_COMMERCE + "/commerce/validate/basket/" + basketId
  );
  if (data && data.status == "VALID") {
    calculateCartItem(data.basket);
  }
  return data;
}

export async function orderDetails(orderId: string) {
  const { data } = await http().get(
    window.env.API_COMMERCE + "/commerce/order-details/" + orderId
  );
  return data;
}
const offersData = z.object({
  active: z.boolean(),
  promotionStartTime: z.string(),
  productIds: z.array(z.string()),
  description: z.string(),
});

export async function featuredOffers() {
  const { data } = await http().get(
    window.env.API_COMMERCE + "/featured-offers"
  );
  if (!data) {
    return;
  }
  return offersData.parse(data);
}
